import useFetch from 'react-fetch-hook';
import Order from './Order';
import viewModel from './ordersViewModel';
import { useState, useEffect, useMemo } from 'react';
import { Wrapper } from './ordersStyles';
import { extractClientId, getClientIdFromPath } from './clientId';

const MAX_CACHED_PAGES = 20;

function setBoundedCache(setCache, key, value) {
  setCache(previous => {
    const next = { ...previous, [key]: value };
    const keys = Object.keys(next);
    if (keys.length > MAX_CACHED_PAGES) {
      delete next[keys[0]];
    }
    return next;
  });
}

function useOrders() {
  // Cache orders per (limit,page) to avoid flicker and enable instant navigation
  const [ordersByKey, setOrdersByKey] = useState({});
  const [shownPage, setShownPage] = useState(1);
  const [shownLimit, setShownLimit] = useState(() => {
    const saved = parseInt(localStorage.getItem('pageLimit') || '20', 10);
    return Number.isNaN(saved) ? 20 : saved;
  });
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const saved = parseInt(localStorage.getItem('pageLimit') || '20', 10);
    return Number.isNaN(saved) ? 20 : saved;
  });
  const [retryKey, setRetryKey] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [paginationByKey, setPaginationByKey] = useState({});
  const [clientId, setClientId] = useState(() => {
    const id = getClientIdFromPath(window.location.pathname);
    if (id) {
      localStorage.setItem('clientId', id);
      return id;
    }
    return localStorage.getItem('clientId') || '';
  });

  const apiUrl = useMemo(() => {
    // keep URL stable; we send page/limit in body
    return (process.env.REACT_APP_API_BASE_URL || '') + '/api/track-package';
  }, []);

  const canFetch = Boolean(clientId);
  const { isLoading, data, error } = useFetch(apiUrl, {
    method: 'POST',
    // include pagination in body
    body: JSON.stringify({ clientId, page, limit }),
    headers: {
      'content-type': 'application/json'
    },
    // wait until clientId is available, allow manual retries
    depends: [canFetch, retryKey]
  });

  useEffect(() => {
    if (!isLoading && !error && data) {
      const shaped = viewModel.shape(data);
      const actualPage =
        data && data.pagination && data.pagination.page
          ? data.pagination.page
          : page;
      const actualLimit =
        data && data.pagination && data.pagination.limit
          ? data.pagination.limit
          : limit;
      const key = `${actualLimit}:${actualPage}`;
      setBoundedCache(setOrdersByKey, key, shaped);
      setPagination(data.pagination || null);
      setBoundedCache(setPaginationByKey, key, data.pagination || null);
      // Only switch the visible (shown) page/limit if this response matches the current requested pair
      if (actualLimit === limit && actualPage === page) {
        setShownPage(page);
        setShownLimit(limit);
      }
    }
  }, [isLoading, error, data, page, limit]);

  // Helper to change pages while keeping current page visible until data for the new page arrives
  const setPageSafe = nextPage => {
    setPage(nextPage);
    // If we already have cached data for that (limit,page), switch immediately for snappy UX
    const key = `${limit}:${nextPage}`;
    if (ordersByKey[key]) {
      setShownPage(nextPage);
      setShownLimit(limit);
    }
  };

  // When limit changes, go to page 1 and persist choice. Keep current page visible until new data arrives.
  useEffect(() => {
    setPage(1);
    localStorage.setItem('pageLimit', String(limit));
  }, [limit]);

  // Reset expanded card when the visible page changes to avoid stale expansion state
  useEffect(() => {
    setExpandedId(null);
  }, [shownPage]);

  // Reset cached states when the clientId changes
  useEffect(() => {
    setOrdersByKey({});
    setPagination(null);
    setPaginationByKey({});
    setExpandedId(null);
    setPage(1);
  }, [clientId]);

  useEffect(() => {
    const handlePopState = () => {
      const nextClientId = getClientIdFromPath(window.location.pathname);
      if (nextClientId) {
        localStorage.setItem('clientId', nextClientId);
      } else {
        localStorage.removeItem('clientId');
      }
      setClientId(nextClientId);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const retryFetch = () => {
    if (!canFetch) {
      return;
    }
    setRetryKey(prev => prev + 1);
  };

  return {
    isLoading,
    hasError: Boolean(error),
    error,
    // data for the currently shown page (could be stale while new page is loading)
    shownOrders: ordersByKey[`${shownLimit}:${shownPage}`] || [],
    expandedId,
    setExpandedId,
    page,
    shownPage,
    shownLimit,
    setPage: setPageSafe,
    pagination,
    paginationByKey,
    limit,
    setLimit,
    retryFetch,
    clientId,
    setClientId
  };
}

function CabinetLinkInput({ initialValue, onSubmit, error }) {
  const [value, setValue] = useState(initialValue || '');

  const triggerSubmit = val => {
    if (onSubmit) {
      onSubmit(val);
    }
  };

  const handleInputChange = e => {
    setValue(e.target.value);
  };

  const handleSubmit = e => {
    e.preventDefault();
    triggerSubmit(value);
  };

  return (
    <div className="cabinet-link-card">
      <p className="eyebrow">Личный кабинет Butler Delivery</p>
      <h2>Отследить посылку</h2>
      <p>
        Вставьте ссылку на кабинет или введите ID — покажем актуальный статус
        всех отправлений.
      </p>
      <form onSubmit={handleSubmit} className="input-group">
        <label htmlFor="client-id-input">Ссылка или ID кабинета</label>
        <div className="form-controls">
          <input
            id="client-id-input"
            type="text"
            value={value}
            onChange={handleInputChange}
            placeholder="Например, K26Z94NRMU"
            required
            aria-invalid={Boolean(error)}
            aria-describedby={
              error ? 'client-id-hint client-id-error' : 'client-id-hint'
            }
          />
          <button type="submit" className="button primary tracking-submit">
            Отследить посылку
          </button>
        </div>
        <p id="client-id-hint" className="input-hint">
          Поддерживается ID или ссылка вида{' '}
          <code>client.butlerdelivery.live/K26Z94NRMU</code>
        </p>
        {error ? (
          <p id="client-id-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function OrderSkeletons() {
  return (
    <div
      className="order-skeletons"
      role="status"
      aria-label="Загрузка заказов"
    >
      <ul className="orders" aria-hidden="true">
        {[0, 1, 2].map(index => (
          <li
            key={index}
            className="order-skeleton"
            data-testid="order-skeleton"
          >
            <div className="order-skeleton-copy">
              <span className="skeleton-block skeleton-title" />
              <span className="skeleton-block skeleton-timestamp" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Orders() {
  const {
    shownOrders,
    isLoading,
    hasError,
    expandedId,
    setExpandedId,
    shownPage,
    shownLimit,
    setPage,
    pagination,
    paginationByKey,
    retryFetch,
    clientId,
    setClientId
  } = useOrders();
  const [inputError, setInputError] = useState('');
  // Use the pagination metadata that corresponds exactly to the currently shown content
  const keyForShown = `${shownLimit}:${shownPage}`;
  const displayedPagination = paginationByKey[keyForShown] || pagination;
  const hasOrders = Boolean(shownOrders && shownOrders.length > 0);
  const shouldShowOrderSkeletons = isLoading && !hasOrders;
  const shouldShowEmptyState =
    Boolean(clientId) && !isLoading && !hasError && !hasOrders;
  const shouldShowLinkInput = !clientId && !isLoading && !hasError;

  const handleClientSubmit = rawInput => {
    const id = extractClientId(rawInput);
    if (!id) {
      setInputError(
        'Введите корректный ID кабинета или ссылку Butler Delivery'
      );
      return;
    }

    setInputError('');
    localStorage.setItem('clientId', id);
    setClientId(id);
    if (window.location.pathname !== `/${id}`) {
      window.history.pushState(null, '', `/${id}`);
    }
    setPage(1);
  };

  return (
    <Wrapper>
      {hasOrders ? (
        <ul className="orders">
          {shownOrders.map(order => (
            <Order
              key={order.id}
              order={order}
              isExpanded={expandedId === order.id}
              onExpand={id => setExpandedId(id)}
            />
          ))}
        </ul>
      ) : shouldShowOrderSkeletons ? (
        <OrderSkeletons />
      ) : shouldShowEmptyState ? (
        <div className="status-block status-empty">Заказы не найдены</div>
      ) : shouldShowLinkInput ? (
        <CabinetLinkInput
          onSubmit={handleClientSubmit}
          initialValue={clientId}
          error={inputError}
        />
      ) : null}
      {hasError ? (
        <div className="status-block status-error">
          <strong>Не удалось получить данные</strong>
          <button
            className="retry-button"
            onClick={retryFetch}
            disabled={isLoading}
          >
            Повторить попытку
          </button>
        </div>
      ) : null}
      {displayedPagination &&
      (displayedPagination.hasPrev ||
        displayedPagination.hasNext ||
        displayedPagination.totalPages > 1) ? (
        <div className="pagination">
          <button
            className="btn"
            disabled={
              isLoading ||
              !displayedPagination.hasPrev ||
              displayedPagination.prevPage == null
            }
            onClick={() =>
              displayedPagination.prevPage != null &&
              setPage(displayedPagination.prevPage)
            }
          >
            « Назад
          </button>

          <button
            className="btn"
            disabled={
              isLoading ||
              !displayedPagination.hasNext ||
              displayedPagination.nextPage == null
            }
            onClick={() =>
              displayedPagination.nextPage != null &&
              setPage(displayedPagination.nextPage)
            }
          >
            Вперёд »
          </button>
        </div>
      ) : null}
    </Wrapper>
  );
}

export default Orders;
